import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    MenuItem,
    Select,
    Tooltip,
    Typography,
} from '@mui/material';
import { Info } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import { replanningPreparationState } from '~/state/atoms/adaptiveReplanningAtoms';
import { COMPACT_ITEM, COMPACT_SELECT } from './replanningControlSx';
import { usePreselectFirst } from './usePreselectFirst';
import { useSyntheticImagingRun } from './useSyntheticImagingRun';

export default function SyntheticImagingStage() {
    const intl = useIntl();
    const [preparation, setPreparation] = useRecoilState(
        replanningPreparationState
    );
    const { models, isRunning: isGenerating } = useSyntheticImagingRun();

    const { generateSynthetic, syntheticModelName, compositeCbct } =
        preparation;

    usePreselectFirst({
        options: generateSynthetic ? models : [],
        hasSelection: !!syntheticModelName,
        onSelect: (model) =>
            setPreparation((current) =>
                current.syntheticModelName
                    ? current
                    : { ...current, syntheticModelName: model.name }
            ),
    });

    return (
        <Box px={1.5} pt={1} pb={1}>
            <FormControlLabel
                control={
                    <Checkbox
                        size="small"
                        checked={generateSynthetic}
                        disabled={isGenerating}
                        onChange={(event) =>
                            setPreparation((current) => ({
                                ...current,
                                generateSynthetic: event.target.checked,
                                compositeCbct: event.target.checked
                                    ? current.compositeCbct
                                    : false,
                            }))
                        }
                        data-testid="replanning-generate-synthetic"
                    />
                }
                label={
                    <Box fontSize={11}>
                        <FormattedMessage id="replanning.prepare.synthetic" />
                    </Box>
                }
            />
            {generateSynthetic && (
                <Box pl={4} display="flex" flexDirection="column" gap={1}>
                    <Typography fontSize={11} color="text.secondary">
                        <FormattedMessage id="replanning.prepare.syntheticModel" />
                    </Typography>
                    <FormControl size="small" fullWidth>
                        <Select
                            displayEmpty
                            disabled={isGenerating}
                            value={syntheticModelName ?? ''}
                            onChange={(event) =>
                                setPreparation((current) => ({
                                    ...current,
                                    syntheticModelName: event.target.value,
                                }))
                            }
                            inputProps={{
                                'aria-label': intl.formatMessage({
                                    id: 'replanning.prepare.syntheticModel',
                                }),
                            }}
                            data-testid="replanning-synthetic-model"
                            sx={COMPACT_SELECT}
                        >
                            {models.length === 0 && (
                                <MenuItem value="" sx={COMPACT_ITEM}>
                                    <FormattedMessage id="replanning.prepare.noSyntheticModel" />
                                </MenuItem>
                            )}
                            {models.map((model) => (
                                <MenuItem
                                    key={model.name}
                                    value={model.name}
                                    sx={COMPACT_ITEM}
                                >
                                    {model.label || model.name}
                                    {(model.inputModalities ?? []).length >
                                        0 && (
                                        <Box
                                            component="span"
                                            fontSize={9.5}
                                            color="text.secondary"
                                        >
                                            (
                                            {(model.inputModalities ?? []).join(
                                                ', '
                                            )}
                                            )
                                        </Box>
                                    )}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Box>
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={compositeCbct}
                                        disabled={isGenerating}
                                        onChange={(event) =>
                                            setPreparation((current) => ({
                                                ...current,
                                                compositeCbct:
                                                    event.target.checked,
                                            }))
                                        }
                                        data-testid="replanning-composite-cbct"
                                    />
                                }
                                label={
                                    <Box fontSize={11}>
                                        <FormattedMessage id="compositeCbct" />
                                    </Box>
                                }
                                sx={{ mr: 0.5 }}
                            />
                            <Tooltip
                                title={
                                    <FormattedMessage id="replanning.prepare.compositeInfo" />
                                }
                                placement="right"
                            >
                                <Box
                                    component="span"
                                    tabIndex={0}
                                    role="img"
                                    aria-label={intl.formatMessage({
                                        id: 'replanning.prepare.compositeInfo',
                                    })}
                                    display="flex"
                                    data-testid="replanning-composite-info"
                                >
                                    <Info size={12} aria-hidden />
                                </Box>
                            </Tooltip>
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
}
